/**
 * `/sync` events for code outside React, which cannot reach the socket through
 * `useNamespaceSocket`: the upload tray (`uploadManagerStore`) waits on these
 * rather than polling the status route.
 *
 * `SocketContext` publishes alongside queuing its cache invalidation, which
 * runs debounced, so a listener must re-read the server rather than trust the
 * query cache to be fresh yet. One `entity` signal per change naming an id, and
 * a `joined` signal every time a sync room is (re)joined, naming its workspace.
 * A listener that sees `joined` must re-read whatever it is waiting on: a write
 * can land while the socket is down or before the first join, and its
 * broadcast is not replayed. A join to ANOTHER workspace means this client has
 * left the room that would carry the listener's next event.
 */
export type SyncSignal =
  | { readonly kind: 'entity'; readonly entityId: string }
  | { readonly kind: 'joined'; readonly workspaceId: string };

type Listener = (signal: SyncSignal) => void;

const listeners = new Set<Listener>();

/** Subscribe; returns the unsubscribe. */
export function onSyncSignal(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Called by `SocketContext` only. A throwing listener never stops the others. */
export function publishSyncSignal(signal: SyncSignal): void {
  for (const listener of [...listeners]) {
    try {
      listener(signal);
    } catch (error) {
      console.error('[SyncSignals] listener failed', error);
    }
  }
}
